import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { generateUuid, Uuid } from '@ftr/contracts/type/shared'
import { SoftCreateUtteranceSttEdit } from '@ftr/regional-api-contracts'
import { Duration } from '@js-joda/core'
import { RealTimeSttSegmentMap } from '../../../types'
import { OperationResult } from './operation-result'
import { bumpStartTimeEditsToProduceUniqueTime } from './unique-start-time-utils'
import { BoundaryError, checkEditForBoundaryErrors } from './within-boundaries-utils'

const DEFAULT_NEW_UTTERANCE_DURATION: Duration = Duration.ofSeconds(1)
export interface CreateRemarkBeforeOperation {
  // e.g. enter pressed at end of remark
  type: 'createRemarkBefore'
  remarkId: Uuid
}

export function createRemarkBeforeTransaction(
  operation: CreateRemarkBeforeOperation,
  segmentMap: RealTimeSttSegmentMap,
): OperationResult {
  // Find the remark in the segment map
  const remark = segmentMap.findRemark(operation.remarkId)
  if (!remark) {
    throw new Error(`Remark with ID ${operation.remarkId} not found in segment map`)
  }

  const newStartTime = remark.startTime.minus(DEFAULT_NEW_UTTERANCE_DURATION)
  const uniqueStartTimeEdits = bumpStartTimeEditsToProduceUniqueTime(newStartTime, segmentMap, 'backward')
  const newUtteranceId = generateUuid()

  const newUtteranceEdit: SoftCreateUtteranceSttEdit = {
    type: 'softCreateUtterance',
    utteranceId: newUtteranceId,
    dialect: remark.dialect ?? null,
    startTime: newStartTime,
    endTime: remark.startTime,
    speakerId: remark.speakerId,
  }
  const boundaryError = checkEditForBoundaryErrors(newUtteranceEdit, segmentMap)
  if (boundaryError) {
    return {
      type: 'noChange',
      reason: getSkippedBoundaryErrorMessage(boundaryError),
    }
  }

  return {
    type: 'transaction',
    transaction: {
      id: generateUuid(),
      edits: [...uniqueStartTimeEdits, newUtteranceEdit],
    },
    sideEffects: [
      {
        type: 'focusRemark',
        remarkId: newUtteranceId,
        selectionIndex: 0,
      },
    ],
  }
}

function getSkippedBoundaryErrorMessage(error: BoundaryError): string {
  switch (error) {
    case 'start-end-different-sessions':
    case 'start-end-not-in-session':
    case 'start-not-in-session':
    case 'end-not-in-session':
      return 'Adding text above isn’t available because the content is out of session.'
    case 'start-end-span-inaccessible-sealed':
    case 'start-end-in-inaccessible-sealed':
    case 'start-in-inaccessible-sealed':
    case 'end-in-inaccessible-sealed':
      return 'Adding text above isn’t available because the content is sealed.'
    default:
      assertUnreachable(error)
  }
}
